import { message } from "antd";
import dayjs from "dayjs";
import { predictStockForVariations } from "../../core/utilities/functions";
import { getCampaignStockActions } from "../../core/api/mediaplan";
export function validateCampaign(campaign) {
  const code = campaign.code || "[no code]";
  if (!campaign.code) {
    message.error(`Code is required for ${code}`);
    return false;
  }
  if (!campaign.brand) {
    message.error(`Brand is required for ${code}`);
    return false;
  }
  if (!campaign.product) {
    message.error(`Product is required for ${code}`);
    return false;
  }
  if (!campaign.variation) {
    message.error(`Variation is required for ${code}`);
    return false;
  }
  if (!campaign.offer) {
    message.error(`Offer is required for ${code}`);
    return false;
  }
 
  if (!campaign.mailDate) {
    message.error(`Mail Date is required for ${code}`);
    return false;
  }
  if (campaign.mailQty == null || campaign.mailQty <= 0) {
    message.error(`Mail Qty must be greater than zero for ${code}`);
    return false;
  }
  if (campaign.rateOfResponse == null || campaign.rateOfResponse <= 0) {
    message.error(`Rate of Response must be greater than zero for ${code}`);
    return false;
  }
  if (!campaign.strategy) {
    message.error(`Strategy is required for ${code}`);
    return false;
  }
  if (!campaign.status) {
    message.error(`Status is required for ${code}`);
    return false;
  }
  return true;
}



export async function generateForCampaignNew(campaign, variationIds = [], startDate = null, endDate = null, options = {}) {
  const { mailDate, itemEstimation, mailQty } = campaign;
  
  // nothing to do?
  if (!mailDate || !itemEstimation.length) {
    return {};
  }
  
  try {
    // Extract variation IDs from item estimations if not provided
    const variations = variationIds && variationIds.length > 0 ? 
      variationIds : 
      itemEstimation.map(item => item.product_variation_id || item.variation);
    
    // Set default dates if not provided
    const start = startDate || dayjs(mailDate);
    const end = endDate || dayjs(mailDate).add(100, 'day');
    
    // We'll create a properly formatted campaign object below instead of this unused campaignData
    
    // Use the extracted values for the API call - similar to getDailyProductOrderPrediction
    const params = new URLSearchParams();
    
    // Add variation IDs
    if (variations && variations.length > 0) {
      variations.forEach(id => params.append("variation_ids[]", id));
    }
    
    // Add date range
    if (start) {
      params.append("start_date", start.format('YYYY-MM-DD'));
    }
    
    if (end) {
      params.append("end_date", end.format('YYYY-MM-DD'));
    }
    
    // Create properly formatted campaign data for the API call
    const formattedCampaign = {
      variation: variations[0], // Use the first variation ID
      mailQty: mailQty,
      rateOfResponse: campaign.rateOfResponse || 0,
      mailDate: typeof mailDate === 'string' ? mailDate : dayjs(mailDate).format('YYYY-MM-DD'),
      itemEstimation: itemEstimation.map(item => ({
        variation: item.product_variation_id || item.variation,
        qtyPerOrder: item.qty_per_order || item.qtyPerOrder || 1,
        offer_item_description: item.offer_item_description || '',
        offer_item_id: item.offer_item_id || null,
        totalQty: item.totalQty || (item.qty_per_order * mailQty) || 0,
        product_variation_id: item.product_variation_id || item.variation
      }))
    };
    
    // Add campaign data in the correct format
    params.append("new_campaign_data", JSON.stringify([formattedCampaign]));
    
    // Call the backend API to get actions with the options (like signal)
    // Use the formatted campaign data for consistency with getDailyProductOrderPrediction
    const response = await getCampaignStockActions(formattedCampaign, options);
    
    if (response && response.data) {
      return response.data;
    }
    
    return {};
    
  } catch (err) {
    console.error("Error generating actions for new campaign:", err);
    return {
      global_warning: [
        {
          type: "api_error",
          description: "Error analyzing stock for new campaign: " + (err.message || "Unknown error"),
          severity: "error",
        },
      ],
    };
  }
}






export async function generateForCampaign(campaign) {

    const { mailDate, itemEstimation, mailStockData, mailQty, clientIds, printStockData} = campaign;

    // nothing to do?
    if (!mailDate || !itemEstimation.length || !mailStockData.length) {
      return {};
    }
  
    const today = dayjs();
    const mailDateObj = dayjs(mailDate);
    const actions = {};
  
  // Check if mail date is in the past
    if (mailDateObj.isBefore(today, "day")) {
      if (campaign.id) {
        actions["global_warning"] = [
          {
            type: "past_mail_date_edit",
            description:
              "This campaign has a mail date in the past. No actions will be generated.",
            severity: "warning",
          },
        ];
        return actions;
      } else {
        actions["global_warning"] = [
          {
            type: "invalid_mail_date",
            description:
              "Mail date cannot be in the past. Please select a future date.",
            severity: "error",
          },
        ];
        return actions;
      }
    }

    for (const item of campaign.itemEstimation) {
      const stockData = mailStockData.find(
        (stock) => stock.variation === item.offer_item_description
      );
      if (!stockData) continue;

      const totalQty = parseFloat(item.totalQty) || 0;
      const stock = stockData.stock || 0;
      const diff = stock - totalQty;
      const itemActions = [];
      const productVariationId = item.product_variation_id;

      if (diff < 0) {
        if (stock < 0) {
          itemActions.push({
            type: "critical_stock",
            description: `Stock for ${item.offer_item_description} is negative (${stock}). Campaign cannot proceed without resolving this issue.`,
            severity: "error",
          });
        }

        // Find a future date (up to 180 days) where predicted stock is sufficient
        let futureDateWithStock = null;
        let bestDateWithOrderNeeded = null;
        
        for (let daysToWait = 1; daysToWait <= 180; daysToWait++) {
          const futureDate = today.add(daysToWait, "day");
          try {
            // Get predicted stock on future date, accounting for all other campaigns
            const futureStockResult = await predictStockForVariations(
              futureDate,
              clientIds,
              [productVariationId]
            );
            const predictedStock = futureStockResult[productVariationId] ?? 0;
            
            // If stock will be sufficient on this date, we can suggest it
            if (predictedStock >= totalQty) {
              futureDateWithStock = {
                date: futureDate.format("YYYY-MM-DD"),
                daysToWait,
                estimatedStock: predictedStock,
              };

              itemActions.push({
                type: "change_mail_date",
                description: `Change mail date to ${
                  futureDateWithStock.date
                } when stock is estimated to be sufficient (${Math.round(
                  predictedStock
                )} units including expected deliveries and usage from other campaigns)`,
                newDate: futureDateWithStock.date,
                newDiff: (predictedStock - totalQty).toFixed(2),
                priority: 1,
              });
              break;
            } 
            // If we don't have a best date with order needed yet, or this date requires less ordering
            else if (daysToWait >= 60) { // Only consider dates at least 60 days away for ordering
              const orderNeeded = Math.ceil((totalQty - predictedStock) * 1.1); // Add 10% buffer
              
              if (!bestDateWithOrderNeeded || orderNeeded < bestDateWithOrderNeeded.orderNeeded) {
                bestDateWithOrderNeeded = {
                  date: futureDate.format("YYYY-MM-DD"),
                  daysToWait,
                  estimatedStock: predictedStock,
                  orderNeeded: orderNeeded
                };
              }
            }
          } catch (err) {
            console.error("Error predicting stock for future date:", err);
          }
        }

        // If we found a date with sufficient stock, we've already added the action above
        // If not, suggest the best date that requires ordering
        if (!futureDateWithStock && bestDateWithOrderNeeded) {
          itemActions.push({
            type: "combined_date_and_order",
            description: `Change mail date to ${bestDateWithOrderNeeded.date} AND order ${bestDateWithOrderNeeded.orderNeeded.toLocaleString()} units to ensure sufficient stock by that date`,
            newDate: bestDateWithOrderNeeded.date,
            orderAmount: bestDateWithOrderNeeded.orderNeeded,
            estimatedStock: bestDateWithOrderNeeded.estimatedStock,
            priority: 2,
          });
        } 
        // If we couldn't find any good date, suggest emergency action
        else if (!futureDateWithStock) {
          const emergencyDate = today.add(60, "day");
          const orderAmount = Math.ceil(Math.abs(diff) * 1.1);

          itemActions.push({
            type: "emergency_combined_action",
            description: `Change mail date to ${emergencyDate.format(
              "YYYY-MM-DD"
            )} (60 days from today) AND order ${orderAmount.toLocaleString()} units immediately`,
            newDate: emergencyDate.format("YYYY-MM-DD"),
            orderAmount,
            newDiff: orderAmount,
            priority: 2,
          });
        }

        // Reduce mail quantity
        if (mailQty > 0 && stock > 0) {
          const reductionFactor = stock / totalQty;
          const suggestedMailQty = Math.floor(mailQty * reductionFactor);

          if (suggestedMailQty > 0 && suggestedMailQty < mailQty) {
            itemActions.push({
              type: "reduce_mail_qty",
              description: `Reduce target mail quantity from ${mailQty.toLocaleString()} to ${suggestedMailQty.toLocaleString()}`,
              newQty: suggestedMailQty,
              newDiff: 0,
              priority: 3,
            });
          }
        }

        // Order more if mail date is ≥ 60 days
        const daysTillMailDate = mailDateObj.diff(today, "day");
        if (daysTillMailDate >= 60) {
          const orderAmount = Math.ceil(Math.abs(diff) * 1.1);
          itemActions.push({
            type: "order_more",
            description: `Order ${orderAmount.toLocaleString()} more units of ${
              item.offer_item_description
            }`,
            orderAmount,
            newDiff: (diff + orderAmount).toFixed(2),
            priority: 4,
          });
        }
      }

      itemActions.sort((a, b) => (a.priority || 99) - (b.priority || 99));
      actions[item.offer_item_description] = itemActions;
    }

    if (printStockData.length > 0) {
      const printItem = printStockData[0];
      const printDiff = printItem.stock - printItem.needed;
      const printActions = [];

      if (printDiff < 0) {
        if (mailQty > 0 && printItem.stock > 0) {
          const suggestedMailQty = Math.floor(printItem.stock * 0.95);
          if (suggestedMailQty > 0 && suggestedMailQty < mailQty) {
            printActions.push({
              type: "reduce_mail_qty_print",
              description: `Reduce target mail quantity from ${mailQty.toLocaleString()} to ${suggestedMailQty.toLocaleString()}`,
              newQty: suggestedMailQty,
              newDiff: (printItem.stock - suggestedMailQty).toFixed(2),
              priority: 1,
            });
          }
        }

        const orderAmount = Math.ceil(Math.abs(printDiff) * 1.1);
        printActions.push({
          type: "order_more_print",
          description: `Order ${orderAmount.toLocaleString()} more print materials for offer ${
            printItem.offer
          }`,
          orderAmount,
          newDiff: 0,
          priority: 2,
        });
      }

      printActions.sort((a, b) => (a.priority || 99) - (b.priority || 99));
      actions["print_stock"] = printActions;
    }

    return actions;
  }
  